// RESPALDO TEMPORAL: la lista vieja (`GET /backoffice/customers` + cartera por lotes) mientras
// `GET /backoffice/customers/table` no esté desplegado. Se borra entero cuando el endpoint esté en dev.
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError } from '@/api/errors';
import {
    customersBackofficeApi,
    type BackofficeCustomerRow,
} from '@/api/services/retail-customer/backoffice';
import { listBorrowersPortfolio, type LoanPortfolioResponse } from '@/api/services/loan-credit';
import { mxDay, mxDayOffset } from '@/lib/dateHelpers';
import {
    withDerived,
    type CreditReadState,
    type CustomerCredit,
    type CustomerListRow,
    type CustomersServerFilters,
} from '../lib/customersList';
import type { CustomersTableResult } from './useCustomers';

/** Días que barre la lista vieja cuando el filtro no pide más atrás. */
export const LIST_WINDOW_DAYS = 180;

/** Ids por llamada a la cartera: el techo del silo. */
const PORTFOLIO_BATCH_SIZE = 100;

/** Inicio de la ventana: el default, o antes si el filtro de fecha pide más atrás. */
export function listWindowFrom(filterFrom: string): string {
    const defaultFrom = mxDayOffset(LIST_WINDOW_DAYS);
    return filterFrom && filterFrom < defaultFrom ? filterFrom : defaultFrom;
}

export type PortfolioFetcher = (ids: string[]) => Promise<LoanPortfolioResponse>;

/** Nivel y crédito vivo por cliente, en lotes de 100 y en paralelo. */
export async function loadCustomerCredits(
    ids: string[],
    fetcher: PortfolioFetcher = listBorrowersPortfolio,
): Promise<Map<string, CustomerCredit>> {
    const batches: string[][] = [];
    for (let i = 0; i < ids.length; i += PORTFOLIO_BATCH_SIZE) {
        batches.push(ids.slice(i, i + PORTFOLIO_BATCH_SIZE));
    }
    const responses = await Promise.all(batches.map((batch) => fetcher(batch)));
    const credits = new Map<string, CustomerCredit>();
    for (const item of responses.flatMap((r) => r.items)) {
        if (item.status === 'UNAVAILABLE' || item.status === 'INVALID_ID') continue;
        const active = item.activeCreditsCount ?? (item.activeCreditId ? 1 : 0);
        credits.set(item.retailCustomerId, {
            level: item.clientLevel ?? null,
            hasActiveCredit: active > 0,
        });
    }
    return credits;
}

export function creditStateOf(isPending: boolean, error: unknown): CreditReadState {
    if (error) {
        return error instanceof ApiError && error.httpStatus === 403 ? 'forbidden' : 'error';
    }
    return isPending ? 'loading' : 'ready';
}

/** Lo que el endpoint de tabla filtra en el servidor, hecho aquí sobre la lista vieja. */
export function applyServerFilters(
    rows: BackofficeCustomerRow[],
    filters: CustomersServerFilters,
): BackofficeCustomerRow[] {
    return rows.filter((row) => {
        if (filters.storeId && row.lastStoreId !== filters.storeId) return false;
        if (filters.sellerId && row.lastSellerId !== filters.sellerId) return false;
        const lastDay = row.lastPurchaseAt === null ? null : mxDay(row.lastPurchaseAt);
        if (filters.from && (lastDay === null || lastDay < filters.from)) return false;
        if (filters.to && (lastDay === null || lastDay > filters.to)) return false;
        return true;
    });
}

const NO_CUSTOMERS: BackofficeCustomerRow[] = [];

/** La lista vieja con la misma forma que la tabla, de una sola página. */
export function useLegacyCustomers(
    filters: CustomersServerFilters,
    enabled: boolean,
): CustomersTableResult {
    const list = useQuery({
        queryKey: ['retail-admin', 'clientes', 'list', listWindowFrom(filters.from ?? '')],
        queryFn: () =>
            customersBackofficeApi.listCustomers({ from: listWindowFrom(filters.from ?? '') }),
        enabled,
        staleTime: 60_000,
    });
    const raw = useMemo(
        () => applyServerFilters(list.data?.items ?? NO_CUSTOMERS, filters),
        [list.data, filters],
    );
    const ids = useMemo(() => [...raw.map((r) => r.retailCustomerId)].sort(), [raw]);
    const credits = useQuery({
        queryKey: ['loan-credit', 'portfolio', 'retail-admin-clientes', ids.join(',')],
        queryFn: () => loadCustomerCredits(ids),
        enabled: enabled && ids.length > 0,
        staleTime: 60_000,
        retry: (failures, error) =>
            !(error instanceof ApiError && error.httpStatus === 403) && failures < 2,
    });
    const rows = useMemo(
        (): CustomerListRow[] =>
            raw.map((row) =>
                withDerived({
                    retailCustomerId: row.retailCustomerId,
                    name: row.name,
                    phone: row.phone,
                    accountOnly: false,
                    originStoreId: null,
                    originStoreName: null,
                    purchaseCount: row.purchaseCount,
                    totalSpentCents: row.totalSpentCents,
                    averageTicketCents: row.averageTicketCents,
                    lastPurchaseAt: row.lastPurchaseAt,
                    favoriteStoreName: row.lastStoreName,
                    credit: credits.data?.get(row.retailCustomerId),
                }),
            ),
        [raw, credits.data],
    );
    return {
        rows,
        source: 'fallback',
        accountOnlyCount: null,
        creditState: creditStateOf(ids.length > 0 && credits.isPending, credits.error),
        hasAggregates: raw.some((r) => r.purchaseCount !== null),
        aggregatesUnavailableReason: list.data?.aggregatesUnavailableReason ?? null,
        windowFrom: filters.from ?? list.data?.windowFrom ?? null,
        truncated: list.data?.truncated ?? false,
        hasNextPage: false,
        isFetchingNextPage: false,
        nextPageError: null,
        fetchNextPage: () => undefined,
        isPending: list.isPending,
        isSuccess: list.isSuccess,
        error: list.error,
        refetch: () => {
            void list.refetch();
        },
    };
}
